import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface UseChunkUploadResult {
  progress: number;
  uploading: boolean;
  error: string | null;
  uploadChunks: (file: File, chunkSize?: number) => Promise<IMergeChunkResponse | null>;
}

function getFileExtension(filename: string) {
  return filename.split('.').pop() || '';
}

export default function useChunkUpload({ uploadChunkService, mergeChunkService }: {
  uploadChunkService: (data: IChunkData) => Promise<void>;
  mergeChunkService: (data: IMergeChunkData) => Promise<IMergeChunkResponse>;
}): UseChunkUploadResult {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // chunkSize 1MB
  // eslint-disable-next-line consistent-return
  /**
   * Uploads a file in chunks.
   * @param {File} file - The file to upload.
   * @param {number} [chunkSize=1024 * 1024] - The size of each chunk in bytes (default: 1MB).
   * @returns {Promise<string | null>} - The path of the uploaded file or null if the upload fails.
   */
  const uploadChunks = async (
    file: File,
    chunkSize = 1000000
  ): Promise<IMergeChunkResponse | null> => {
    setProgress(0);
    setUploading(true);
    setError(null);

    const totalChunks = Math.ceil(file.size / chunkSize);

    const qquuid = uuidv4();

    try {
      for (let i = 0; i < totalChunks; i += 1) {
        const start = i * chunkSize;
        const end = Math.min(file.size, start + chunkSize);
        const chunk = file.slice(start, end);

        // eslint-disable-next-line no-await-in-loop
        await uploadChunkService({ qquuid, qqfile: chunk });

        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      // Merge chunks
      const fileExt = getFileExtension(file.name);
      const path = await mergeChunkService({
        chunkedUuid: qquuid,
        fileExtension: fileExt,
        fileName: file.name.split('.').slice(0, -1).join('.'),
      });

      setUploading(false);
      setProgress(100);
      return path;
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
      setUploading(false);
      throw err;
    }
  };

  return {
    progress, uploading, error, uploadChunks
  };
}
